import { cn } from '../../lib/cn.js'

export interface MoneyProps {
  /** **Cents**, as every amount on this platform is stored. */
  cents: number | null | undefined
  empty?: string
  /**
   * Writes a minus for a negative amount rather than dropping it.
   *
   * On by default. A refund row reading `$48.00` beside a charge reading `$48.00`
   * is two charges as far as anybody reading the screen is concerned.
   */
  signed?: boolean
  /** Draws a negative in the error ink. Off where a column is all refunds anyway. */
  tone?: boolean
  className?: string
}

/**
 * Cents, shown as money.
 *
 * The pair to `MoneyInput`, and it exists for the same reason and one more. The
 * API returns cents from ten different fields the desk displays — `amount_cents`,
 * `refunded_cents`, `platform_fee_cents`, `taken_cents` and the rest — and without
 * this, every table divides by a hundred itself. Ten call sites, each free to
 * forget the thousands separator, the second decimal place, or the minus sign.
 *
 * Grouped and always two decimals, because a column of money is read by comparing
 * the shapes of the numbers and `$1842.5` does not line up with `$1,842.50`.
 *
 * **This was missed for seven passes**, including one that assembled nine screens
 * out of this layer — because the harness wrote `"$1,842.50"` into a `StatCard` by
 * hand. Building a screen only finds what the builder does not quietly do for it.
 */
/**
 * The same figure as a plain string, for the places that cannot take an element.
 *
 * A button's label, an `aria-label`, a confirmation title. 9.3a's button reads
 * *"Refund $122.50"*, and without this the screen would format the amount a
 * second time beside the component whose whole purpose is that nothing does.
 */
export function moneyText(cents: number): string {
  const text = (Math.abs(cents) / 100).toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    currencyDisplay: 'narrowSymbol',
  })
  return cents < 0 ? `−${text}` : text
}

export function Money({ cents, empty = '–', signed = true, tone, className }: MoneyProps) {
  if (cents === null || cents === undefined) {
    return <span className={cn('text-ink-muted', className)}>{empty}</span>
  }

  const negative = cents < 0
  const text = (Math.abs(cents) / 100).toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    // `en-CA` with CAD prints `$1,842.50`, which is what the boards show. Not
    // `CA$`: the desk is Canadian and nobody writes the country on their own money.
    currencyDisplay: 'narrowSymbol',
  })

  return (
    <span
      className={cn('tabular-nums', negative && tone && 'text-error', className)}
      // The figure without the grouping, for anything reading the page aloud or
      // copying it out — a screen reader saying "one comma eight four two" is worse
      // than the number.
      data-cents={cents}
    >
      {negative && signed ? `−${text}` : text}
    </span>
  )
}
